import {
  ActionIcon,
  Avatar,
  Badge,
  Button,
  Card,
  Divider,
  Group,
  Modal,
  Pagination,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import { useDebouncedValue, useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import {
  IconArrowUpRight,
  IconBriefcase,
  IconPhone,
  IconQrcode,
  IconSearch,
  IconTrash,
  IconUserPlus,
  IconUsers,
} from "@/lib/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { EmptyState } from "@/components/EmptyState";
import { deletePatient, listPatients, type PatientRead } from "@/lib/api/patients";
import { useAuthStore } from "@/stores/auth";

const PAGE_SIZE = 20;

export function PatientListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canCreate = useAuthStore((s) => s.hasPermission("patient.create"));
  const canDelete = useAuthStore((s) => s.hasPermission("patient.delete"));
  const [query, setQuery] = useState("");
  const [debouncedQuery] = useDebouncedValue(query, 300);
  const [page, setPage] = useState(1);
  const [deleting, setDeleting] = useState<PatientRead | null>(null);
  const [deleteOpen, deleteHandlers] = useDisclosure(false);
  const [deleteReason, setDeleteReason] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["patients", debouncedQuery, page],
    queryFn: () =>
      listPatients({
        q: debouncedQuery || undefined,
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
      }),
  });

  const deleteMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      deletePatient(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["patients"] });
      queryClient.invalidateQueries({ queryKey: ["patients-all"] });
      notifications.show({ color: "green", message: t("patients.deleted") });
      deleteHandlers.close();
      setDeleting(null);
      setDeleteReason("");
    },
    onError: () => {
      notifications.show({ color: "red", message: t("patients.delete_failed") });
    },
  });

  const totalPages = data ? Math.max(Math.ceil(data.total / PAGE_SIZE), 1) : 1;

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-start">
        <Stack gap={4}>
          <Title order={1} fw={800} size="h1" lh={1.1}>
            {t("patients.title")}
          </Title>
          {data && (
            <Group gap={8} mt={4}>
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 999,
                  background: "var(--mantine-color-brand-6)",
                  display: "inline-block",
                  boxShadow: "0 0 0 4px rgba(59, 130, 246, 0.16)",
                }}
              />
              <Text c="brand.7" fw={600} size="sm">
                {t("patients.subtitle_count", { total: data.total, page })}
              </Text>
            </Group>
          )}
        </Stack>
        {canCreate && (
          <Button
            component={Link}
            to="/patients/new"
            size="md"
            radius="xl"
            leftSection={<IconUserPlus size={18} strokeWidth={1.8} />}
            variant="gradient"
            gradient={{ from: "brand.6", to: "brand.8", deg: 135 }}
            style={{ boxShadow: "0 8px 24px rgba(37, 99, 235, 0.25)" }}
          >
            {t("patients.add_new")}
          </Button>
        )}
      </Group>

      <TextInput
        size="lg"
        radius="xl"
        rightSection={<IconSearch size={20} strokeWidth={1.8} />}
        placeholder={t("patients.search_placeholder")}
        value={query}
        onChange={(e) => {
          setQuery(e.currentTarget.value);
          setPage(1);
        }}
        styles={{
          input: {
            border: "none",
            background: "white",
            boxShadow: "0 1px 3px rgba(15,23,42,0.04)",
            height: 56,
            fontSize: 15,
          },
        }}
      />

      <Stack gap="md">
        {isLoading && (
          <Card padding="xl">
            <Text c="dimmed" ta="center">
              {t("common.loading")}
            </Text>
          </Card>
        )}

        {data?.items.length === 0 && !isLoading && (
          <Card padding="xl">
            <EmptyState
              icon={<IconUsers size={32} strokeWidth={1.5} />}
              title={t("patients.empty")}
              action={
                canCreate && (
                  <Button
                    component={Link}
                    to="/patients/new"
                    leftSection={<IconUserPlus size={16} strokeWidth={1.8} />}
                    radius="xl"
                  >
                    {t("patients.add_new")}
                  </Button>
                )
              }
            />
          </Card>
        )}

        {data?.items.map((p) => (
          <PatientRow
            key={p.id}
            patient={p}
            onOpen={() => navigate(`/patients/${p.id}`)}
            canDelete={canDelete}
            onDelete={() => {
              setDeleting(p);
              setDeleteReason("");
              deleteHandlers.open();
            }}
          />
        ))}
      </Stack>

      {data && data.total > PAGE_SIZE && (
        <Group justify="center" mt="md">
          <Pagination total={totalPages} value={page} onChange={setPage} radius="xl" />
        </Group>
      )}

      <Modal
        opened={deleteOpen}
        onClose={() => {
          deleteHandlers.close();
          setDeleting(null);
        }}
        title={t("patients.delete_title")}
        size="md"
        radius="lg"
      >
        <Stack>
          <Text size="sm">
            {t("patients.delete_confirm", {
              name: deleting ? `${deleting.family_name} ${deleting.given_name}` : "",
            })}
          </Text>
          <Textarea
            label={t("patients.delete_reason")}
            placeholder={t("patients.delete_reason_placeholder")}
            minRows={3}
            autosize
            required
            value={deleteReason}
            onChange={(e) => setDeleteReason(e.currentTarget.value)}
          />
          <Group justify="flex-end" mt="sm">
            <Button variant="subtle" color="gray" onClick={deleteHandlers.close}>
              {t("common.cancel")}
            </Button>
            <Button
              color="red"
              loading={deleteMutation.isPending}
              disabled={deleteReason.trim().length < 10}
              leftSection={<IconTrash size={16} strokeWidth={2.2} />}
              onClick={() =>
                deleting &&
                deleteMutation.mutate({ id: deleting.id, reason: deleteReason.trim() })
              }
            >
              {t("patients.delete_confirm_button")}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

function PatientRow({
  patient,
  onOpen,
  canDelete,
  onDelete,
}: {
  patient: PatientRead;
  onOpen: () => void;
  canDelete: boolean;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  const initials = `${patient.family_name[0] ?? ""}${patient.given_name[0] ?? ""}`.toUpperCase();

  return (
    <Card
      padding="lg"
      radius="xl"
      style={{
        cursor: "pointer",
        transition: "transform 120ms ease, box-shadow 120ms ease",
      }}
      onClick={onOpen}
    >
      <Group justify="space-between" wrap="nowrap" align="center" gap="lg">
        <Group gap="md" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
          <Avatar
            size={56}
            radius="xl"
            color="gray"
            styles={{
              root: { background: "var(--mantine-color-gray-1)" },
              placeholder: {
                color: "var(--mantine-color-gray-7)",
                fontWeight: 700,
                fontSize: 16,
                letterSpacing: "0.02em",
              },
            }}
          >
            {initials || "??"}
          </Avatar>
          <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
            <Group gap="sm" wrap="nowrap">
              <Text fw={700} size="md" truncate>
                {patient.family_name} {patient.given_name}
              </Text>
              <Badge
                variant="light"
                color="gray"
                radius="sm"
                size="sm"
                style={{ fontFamily: "monospace", fontWeight: 500 }}
              >
                #{patient.card_number}
              </Badge>
            </Group>
            <Group gap={6} wrap="nowrap">
              <IconPhone size={14} strokeWidth={1.8} color="var(--mantine-color-brand-6)" />
              <Text size="sm" c="brand.7" fw={500} ff="monospace">
                {patient.phone_primary}
              </Text>
            </Group>
          </Stack>
        </Group>

        {(patient.workplace || patient.position) && (
          <>
            <Divider orientation="vertical" />
            <Stack
              gap={4}
              style={{ flex: 1, minWidth: 0, maxWidth: 320 }}
              visibleFrom="sm"
            >
              {patient.workplace && (
                <Group gap={6} wrap="nowrap">
                  <IconBriefcase
                    size={14}
                    strokeWidth={1.8}
                    color="var(--mantine-color-brand-6)"
                  />
                  <Text
                    size="xs"
                    fw={700}
                    c="brand.8"
                    tt="uppercase"
                    style={{ letterSpacing: "0.04em" }}
                  >
                    {t("patients.fields.workplace")}
                  </Text>
                </Group>
              )}
              {patient.workplace && (
                <Text size="sm" fw={600} c="dark.7" truncate>
                  {patient.workplace}
                </Text>
              )}
              {patient.position && (
                <Badge
                  variant="light"
                  color="violet"
                  radius="sm"
                  size="sm"
                  style={{ alignSelf: "flex-start", fontWeight: 500 }}
                >
                  {patient.position}
                </Badge>
              )}
            </Stack>
          </>
        )}

        <Group gap="sm" wrap="nowrap" onClick={(e) => e.stopPropagation()}>
          <ActionIcon
            size={42}
            radius="lg"
            variant="light"
            color="brand"
            onClick={onOpen}
            aria-label="QR"
          >
            <IconQrcode size={20} strokeWidth={1.6} />
          </ActionIcon>
          {canDelete && (
            <ActionIcon
              size={42}
              radius="lg"
              variant="light"
              color="red"
              onClick={onDelete}
              aria-label={t("patients.delete")}
            >
              <IconTrash size={18} strokeWidth={1.8} />
            </ActionIcon>
          )}
          <Button
            size="sm"
            radius="xl"
            color="dark.8"
            rightSection={<IconArrowUpRight size={16} strokeWidth={2} />}
            onClick={onOpen}
          >
            {t("patients.open_card")}
          </Button>
        </Group>
      </Group>
    </Card>
  );
}
